import React from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import moment from "moment";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

// Custom label renderer for better mobile display
const renderCustomLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
  name,
  value,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  // Only show label if percentage is significant enough (> 5%)
  if (percent < 0.05) return null;

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      style={{ fontSize: "11px", fontWeight: "bold" }}
    >
      {value}
    </text>
  );
};

const ReportCharts = ({ reportData }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      {/* Bookings by Source Pie Chart */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Bookings by Source</h3>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={reportData?.summary?.bySource || []}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={80}
              fill="#8884d8"
              dataKey="count"
            >
              {(reportData?.summary?.bySource || []).map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ fontSize: "12px" }}
              formatter={(value, name, props) => [
                `${value}`,
                props.payload.bookingSource,
              ]}
            />
            <Legend
              wrapperStyle={{ fontSize: "11px" }}
              formatter={(value, entry) => entry.payload.bookingSource}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Bookings by Status Pie Chart */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Bookings by Status</h3>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={reportData?.summary?.byStatus || []}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={80}
              fill="#8884d8"
              dataKey="count"
            >
              {(reportData?.summary?.byStatus || []).map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ fontSize: "12px" }}
              formatter={(value, name, props) => [
                `${value}`,
                props.payload.status,
              ]}
            />
            <Legend
              wrapperStyle={{ fontSize: "11px" }}
              formatter={(value, entry) => entry.payload.status}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Daily Trend Line Chart */}
      <div className="bg-white rounded-lg shadow-md p-6 lg:col-span-2">
        <h3 className="text-lg font-semibold mb-4">Daily Booking Trend</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={reportData?.dailyTrend || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(date) => moment(date).format("DD MMM")}
            />
            <YAxis />
            <Tooltip
              labelFormatter={(date) => moment(date).format("DD MMMM YYYY")}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="count"
              stroke="#3b82f6"
              strokeWidth={2}
              name="Bookings"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Bookings by Doctor Bar Chart */}
      <div className="bg-white rounded-lg shadow-md p-6 lg:col-span-2">
        <h3 className="text-lg font-semibold mb-4">Bookings by Doctor</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={reportData?.byDoctor || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="doctorName"
              angle={-45}
              textAnchor="end"
              height={100}
              style={{ fontSize: "12px" }}
            />
            <YAxis style={{ fontSize: "12px" }} />
            <Tooltip />
            <Legend
              wrapperStyle={{ fontSize: "12px", paddingTop: "10px" }}
              iconSize={10}
            />
            <Bar dataKey="count" fill="#3b82f6" name="Bookings" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Bookings by Department Bar Chart */}
      <div className="bg-white rounded-lg shadow-md p-6 lg:col-span-2">
        <h3 className="text-lg font-semibold mb-4">Bookings by Department</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={reportData?.byDepartment || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="departmentName" style={{ fontSize: "12px" }} />
            <YAxis style={{ fontSize: "12px" }} />
            <Tooltip />
            <Legend
              wrapperStyle={{ fontSize: "12px", paddingTop: "10px" }}
              iconSize={10}
            />
            <Bar dataKey="count" fill="#10b981" name="Bookings" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ReportCharts;
